/**
 * Inline SVG doodles — intentionally wonky, hand-drawn feel.
 * No external dependencies, stays crisp at any size.
 */
export function CabbageBlob({ className = "", stroke = "#1A3626", fill = "#93C065" }) {
  return (
    <svg viewBox="0 0 120 120" className={className} aria-hidden>
      <path
        d="M18 62c-4-24 14-44 38-44s46 16 44 42-24 44-48 40-30-14-34-38z"
        fill={fill}
        stroke={stroke}
        strokeWidth="3"
      />
      <path
        d="M40 58c4-12 14-20 26-20M54 80c10 4 22 2 30-6M70 40c2 8 0 18-6 24"
        stroke={stroke}
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="48" cy="58" r="2.2" fill={stroke} />
      <circle cx="74" cy="54" r="2.2" fill={stroke} />
      <path
        d="M52 72c6 4 14 4 20 0"
        stroke={stroke}
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

export function UpsettingDog({ className = "", stroke = "#1A3626" }) {
  return (
    <svg viewBox="0 0 160 140" className={className} aria-hidden>
      <path
        d="M20 80c0-24 20-46 50-46s54 18 58 42c4 18-10 32-30 34l-6 14-14-12-30-2c-18-2-28-14-28-30z"
        fill="#F9F9F4"
        stroke={stroke}
        strokeWidth="3"
      />
      <ellipse cx="58" cy="74" rx="5" ry="7" fill={stroke} />
      <ellipse cx="96" cy="70" rx="5" ry="7" fill={stroke} />
      <path
        d="M70 92c8 4 16 4 24-2"
        stroke={stroke}
        strokeWidth="2.8"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M22 46c2-12 12-18 22-20M138 50c-4-14-16-20-26-22"
        stroke={stroke}
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M84 100v10"
        stroke={stroke}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SpiralStar({ className = "", stroke = "#1A3626" }) {
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden>
      <path
        d="M50 8l10 28 28 4-22 18 8 28-24-16-24 16 8-28-22-18 28-4z"
        fill="#F9F9F4"
        stroke={stroke}
        strokeWidth="3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SquigglyUnderline({ className = "", stroke = "#93C065" }) {
  return (
    <svg viewBox="0 0 300 20" className={className} aria-hidden preserveAspectRatio="none">
      <path
        d="M4 12c20-14 40 14 60 0s40-14 60 0 40 14 60 0 40-14 60 0 40 14 52 0"
        fill="none"
        stroke={stroke}
        strokeWidth="5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function BadFrog({ className = "", stroke = "#1A3626" }) {
  return (
    <svg viewBox="0 0 200 220" className={className} aria-hidden>
      {/* notebook grid lines crossing through (very feint) */}
      <line x1="0" y1="78" x2="200" y2="78" stroke={stroke} strokeWidth="2.5" opacity="0.95" />
      <line x1="158" y1="0" x2="158" y2="220" stroke={stroke} strokeWidth="2.5" opacity="0.95" />

      {/* two arched eyebrow / hair lines (uneven) */}
      <path
        d="M52 60c8-22 34-26 56-12"
        stroke={stroke}
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M138 38c10 4 16 14 14 26"
        stroke={stroke}
        strokeWidth="6"
        strokeLinecap="round"
        fill="none"
      />

      {/* lopsided egg-shaped body (single outline) */}
      <path
        d="M40 110c4-38 36-58 70-54s58 28 54 66c-3 30-22 50-50 56-22 4-46-2-58-22"
        fill="none"
        stroke={stroke}
        strokeWidth="6"
        strokeLinecap="round"
      />

      {/* pointed chin / legs (V shape) */}
      <path
        d="M76 174l24 30 36-22"
        fill="none"
        stroke={stroke}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* two simple oval pupils — no white sclera */}
      <ellipse cx="86" cy="118" rx="9" ry="14" fill={stroke} />
      <ellipse cx="138" cy="116" rx="9" ry="14" fill={stroke} />

      {/* off-centre wonky smile */}
      <path
        d="M104 156q14 18 36 -2"
        fill="none"
        stroke={stroke}
        strokeWidth="6"
        strokeLinecap="round"
      />

      {/* tiny chin line below smile */}
      <line
        x1="124"
        y1="172"
        x2="124"
        y2="190"
        stroke={stroke}
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function HandArrow({ className = "", stroke = "#1A3626" }) {
  return (
    <svg viewBox="0 0 160 80" className={className} aria-hidden>
      <path
        d="M8 50c12-22 36-30 60-22s40 30 60 24"
        fill="none"
        stroke={stroke}
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <path
        d="M118 38l14 14-18 8"
        fill="none"
        stroke={stroke}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Scribble({ className = "", stroke = "#1A3626" }) {
  return (
    <svg viewBox="0 0 200 60" className={className} aria-hidden>
      <path
        d="M6 30c14-22 28 18 44-2s26 18 42-2 26 18 42-2 24 14 60-6"
        fill="none"
        stroke={stroke}
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function StarBlob({ className = "", stroke = "#1A3626", fill = "#93C065" }) {
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden>
      <path
        d="M50 6c4 14 14 22 30 24-12 6-22 14-26 28-4-14-14-22-30-24 14-4 22-14 26-28z"
        fill={fill}
        stroke={stroke}
        strokeWidth="3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function HauntedSandwich({ className = "", stroke = "#1A3626" }) {
  return (
    <svg viewBox="0 0 140 120" className={className} aria-hidden>
      <path
        d="M14 70c0-12 26-36 56-36s56 24 56 36-26 24-56 24-56-12-56-24z"
        fill="#EBE7DD"
        stroke={stroke}
        strokeWidth="3"
      />
      <path
        d="M14 80c10 14 40 20 56 20s46-6 56-20"
        fill="#93C065"
        stroke={stroke}
        strokeWidth="3"
      />
      <circle cx="54" cy="60" r="2.5" fill={stroke} />
      <circle cx="86" cy="58" r="2.5" fill={stroke} />
      <path
        d="M58 74q12 8 26-2"
        stroke={stroke}
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}
